import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useCallback, useContext, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { Box, ColorContext, useScreenResolution } from 'dash-ui';
import { Chart, Tooltip, ArcElement, ScriptableContext } from 'chart.js';

import { GetFileDownloadsStatisticQuery } from 'gql/generated.graphql';

import { getChartSliceSizes } from './DoughnutChart.helpers';

Chart.register(ArcElement, Tooltip);

const DoughnutChart = ({
  data,
  totalCount = 0,
}: {
  data: GetFileDownloadsStatisticQuery;
  totalCount?: number;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { gradientColorSets } = useContext(ColorContext);
  const {
    devices: { isMobile },
  } = useScreenResolution();

  const { collection = [] } = useMemo(
    () => data?.getFileDownloadsStatistic || {},
    [data]
  );

  const { sliceSizes, counts } = useMemo(
    () => getChartSliceSizes(collection.map((item) => item.count)),
    [collection]
  );

  const sliceThickness = useMemo(
    () => ({
      id: 'sliceThickness',
      beforeDraw: (chart: any) => {
        sliceSizes.forEach((item, index) => {
          chart.getDatasetMeta(0).data[index].outerRadius =
            (chart.width / (isNaN(item) ? 200 : 300 - item)) * 100;
        });
      },
    }),
    [sliceSizes]
  );

  const doughnutLabel = useMemo(
    () => ({
      id: 'doughnutLabel',
      beforeDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx;

        ctx.save();
        const x = chart.getDatasetMeta(0).data[0].x;
        const y = chart.getDatasetMeta(0).data[0].y;
        ctx.font = 'bold 24px Work Sans sans-serif';
        ctx.fillStyle = theme.colors.n90;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(totalCount, x, y);

        ctx.font = '12px Work Sans sans-serif';
        ctx.fillStyle = theme.colors.n70;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          t('pages:organizationDashboardPage.totalValue'),
          x,
          y + 20
        );
      },
    }),
    [totalCount, theme, t]
  );

  const innerCircle = useMemo(
    () => ({
      id: 'innerCircle',
      afterDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx;
        ctx.save();
        const x = chart.getDatasetMeta(0).data[0].x;
        const y = chart.getDatasetMeta(0).data[0].y;
        const innerRadius = chart.getDatasetMeta(0).data[0].innerRadius;

        ctx.beginPath();
        ctx.arc(x, y, innerRadius - 5, 0, 2 * Math.PI);
        ctx.strokeStyle = theme.colors.n30;
        ctx.stroke();
      },
    }),
    [theme]
  );

  const generateGradients = useCallback(
    (chart: ScriptableContext<'doughnut'>) => {
      const ctx = chart.chart.ctx;
      const gradients: any = [];

      gradientColorSets.forEach(([stop1, stop2]) => {
        const gradient = ctx.createLinearGradient(0, 0, 0, 200);
        gradient.addColorStop(0, stop1);
        gradient.addColorStop(1, stop2);
        gradients.push(gradient);
      });

      return gradients;
    },
    [gradientColorSets]
  );

  return (
    <Box w={(isMobile) ? 250 : 356} h={(isMobile) ? 250 : 356}>
      <Doughnut
        data={{
          datasets: [
            {
              data: counts,
              backgroundColor: generateGradients,
            },
          ],

          // These labels appear in the legend and in the tooltips when hovering different arcs
          labels: collection.map(({ file }) => file.name),
        }}
        plugins={[sliceThickness, doughnutLabel, innerCircle]}
      />
    </Box>
  );
};

export default DoughnutChart;
